import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Fragment, type ReactNode } from 'react'
import styled, { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { BottomSheet } from '@/@bothrs-ui/web/components/bottom-sheet/BottomSheet'
import { FlexRow, Padding } from '@/@bothrs-ui/web/components/layout'
import { Modal } from '@/@bothrs-ui/web/components/modal/Modal'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { useStructuredObservationDialog } from '../StructuredObservationList.context'

type StructuredObservationItemDialogueProps = {
  title: string | ((mode: 'insert' | 'update') => string)
  children: ReactNode
}

export const StructuredObservationItemDialogue = ({ title, children }: StructuredObservationItemDialogueProps) => {
  const theme = useTheme()
  const { dialog, closeDialog } = useStructuredObservationDialog()

  const isMobile = useMediaQuery(breakpoints.toS) || Capacitor.isNativePlatform()

  const isOpen = dialog.mode !== 'closed'
  const dialogKey = dialog.mode === 'update' ? `${dialog.mode}:${dialog.itemId}` : dialog.mode
  const resolvedTitle = typeof title === 'function' ? title(dialog.mode === 'closed' ? 'insert' : dialog.mode) : title

  if (isMobile) {
    return (
      <BottomSheet steps={[100]} visible={isOpen} onClose={closeDialog}>
        <Padding px={theme.UI.Spacing.Space4} pb={theme.UI.Spacing.Space6} style={{ height: '100%' }}>
          <Fragment key={dialogKey}>{children}</Fragment>
        </Padding>
      </BottomSheet>
    )
  }

  return (
    <Modal isVisible={isOpen} onClose={closeDialog} maxHeight='90vh'>
      <StModalContent>
        <FlexRow justifyContent='space-between' alignItems='center'>
          <H2 noMargin>{resolvedTitle}</H2>
          <FontAwesomeIcon
            icon={['far', 'close']}
            fontSize={20}
            color={theme.Colors.Neutrals.Darker}
            onClick={closeDialog}
            style={{ cursor: 'pointer' }}
          />
        </FlexRow>
        <Fragment key={dialogKey}>{children}</Fragment>
      </StModalContent>
    </Modal>
  )
}

// ================================ Styled Components ================================

const StModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
`
